import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 luxury-hover",
  {
    variants: {
      variant: {
        default: "bg-brand-blue text-brand-blue-foreground hover:bg-accent hover:text-accent-foreground shadow-lg font-semibold",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-medium",
        outline: "border-2 border-brand-blue bg-transparent text-brand-blue hover:bg-brand-blue hover:text-brand-blue-foreground shadow-soft",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-soft",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-brand-blue underline-offset-4 hover:underline",
        accent: "bg-accent text-accent-foreground hover:bg-accent-light font-semibold shadow-medium",
        heroSecondary: "bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground border-2 border-primary-foreground/30",
        premium: "bg-brand-blue text-brand-blue-foreground hover:bg-accent hover:text-accent-foreground font-tech font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1",
        luxury: "glass border border-accent/30 text-accent backdrop-blur-xl hover:bg-accent hover:text-primary hover:border-accent shadow-luxury",
        gold: "bg-accent text-accent-foreground hover:bg-accent-light font-semibold shadow-lg",
      },
      size: {
        default: "h-11 px-6 py-3 min-h-[44px]",
        sm: "h-9 rounded-lg px-4 min-h-[44px]",
        lg: "h-14 rounded-xl px-10 py-4 text-lg min-h-[44px]",
        xl: "h-16 rounded-xl px-12 py-5 text-xl min-h-[44px]",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
